import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils";
/**
 * Tabs — pill-switcher tab set, dependency-free.
 *
 * `Tabs` holds the selection, `TabsList` is the pill-shaped strip of triggers,
 * `TabsTrigger` is one tab, and `TabsPanel` is the region a tab reveals. Use it
 * controlled (`value` + `onValueChange`) or uncontrolled (`defaultValue`).
 *
 * The moving pill is the design's own indicator, ported from the prototype's
 * `SlidingPillIndicator`. It lives INSIDE `TabsList` rather than being a
 * component consumers place themselves: it has to be a sibling of the triggers
 * it measures, so making that the list's job removes the one way to wire it up
 * wrong. It is a single absolutely-positioned span that translates and resizes
 * to the active trigger, so the strip animates as one object instead of each
 * tab cross-fading its own background.
 *
 * Selection follows focus (APG automatic activation): panels here are already
 * in the document, so there is nothing to defer and arrowing through the strip
 * should show what it lands on.
 */
/** Matches the prototype's `--pill-glide-duration`. */
const GLIDE_MS = 420;
const TabsCtx = React.createContext(null);
function useTabs(component) {
    const ctx = React.useContext(TabsCtx);
    if (!ctx)
        throw new Error(`${component} must be used inside <Tabs>`);
    return ctx;
}
const triggerId = (baseId, value) => `${baseId}-tab-${value}`;
const panelId = (baseId, value) => `${baseId}-panel-${value}`;
/**
 * `useLayoutEffect` that degrades to `useEffect` on the server. The pill has to
 * measure after commit but before paint or it visibly jumps on first render;
 * React warns about the layout variant during SSR, where there is nothing to
 * measure anyway.
 */
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;
const Tabs = React.forwardRef(({ value, defaultValue, onValueChange, className, children, ...props }, ref) => {
    const [internal, setInternal] = React.useState(defaultValue ?? "");
    const resolved = value ?? internal;
    const baseId = React.useId();
    const setValue = React.useCallback((next) => {
        if (value === undefined)
            setInternal(next);
        onValueChange?.(next);
    }, [value, onValueChange]);
    const ctx = React.useMemo(() => ({ value: resolved, setValue, baseId }), [resolved, setValue, baseId]);
    return (_jsx(TabsCtx.Provider, { value: ctx, children: _jsx("div", { ref: ref, className: cn("w-full", className), ...props, children: children }) }));
});
Tabs.displayName = "Tabs";
/**
 * Tracks the active trigger's box and writes it to the indicator as custom
 * properties, so the pill transitions between measurements rather than being
 * re-rendered at each one.
 *
 * Re-measures on three signals because each moves the triggers independently:
 * the selection changing, the strip or a trigger resizing (`ResizeObserver`),
 * and webfonts arriving — the last one matters most, since the fallback face
 * sizes the labels differently and the pill would otherwise sit at the width
 * the fallback needed.
 */
function useSlidingPill(listRef, indicatorRef, value) {
    const [measured, setMeasured] = React.useState(false);
    useIsomorphicLayoutEffect(() => {
        const list = listRef.current;
        const indicator = indicatorRef.current;
        if (!list || !indicator)
            return;
        const triggers = Array.from(list.querySelectorAll("[data-tab-value]"));
        const active = triggers.find((el) => el.dataset.tabValue === value);
        if (!active)
            return;
        const sync = () => {
            indicator.style.setProperty("--tab-pill-x", `${active.offsetLeft}px`);
            indicator.style.setProperty("--tab-pill-y", `${active.offsetTop}px`);
            indicator.style.setProperty("--tab-pill-w", `${active.offsetWidth}px`);
            indicator.style.setProperty("--tab-pill-h", `${active.offsetHeight}px`);
        };
        sync();
        setMeasured(true);
        const observer = new ResizeObserver(sync);
        observer.observe(list);
        triggers.forEach((el) => observer.observe(el));
        void document.fonts?.ready.then(sync);
        return () => observer.disconnect();
    }, [listRef, indicatorRef, value]);
    return measured;
}
const TabsList = React.forwardRef(({ label, className, children, ...props }, forwardedRef) => {
    const { value, setValue } = useTabs("TabsList");
    const listRef = React.useRef(null);
    const indicatorRef = React.useRef(null);
    React.useImperativeHandle(forwardedRef, () => listRef.current, []);
    const measured = useSlidingPill(listRef, indicatorRef, value);
    // Read the triggers out of the DOM rather than having them register through
    // context: the pill already depends on their live layout, so one source of
    // truth for "what is in this strip, in order" keeps the two in step.
    const onKeyDown = (event) => {
        const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
        if (!keys.includes(event.key))
            return;
        const triggers = Array.from(listRef.current?.querySelectorAll("[data-tab-value]:not([disabled])") ?? []);
        if (triggers.length === 0)
            return;
        const current = triggers.findIndex((el) => el.dataset.tabValue === value);
        const last = triggers.length - 1;
        const next = event.key === "Home"
            ? 0
            : event.key === "End"
                ? last
                : event.key === "ArrowLeft"
                    ? current <= 0
                        ? last
                        : current - 1
                    : current >= last
                        ? 0
                        : current + 1;
        event.preventDefault();
        const target = triggers[next];
        setValue(target.dataset.tabValue);
        target.focus();
    };
    return (_jsxs("div", { ref: listRef, role: "tablist", "aria-label": label, "aria-orientation": "horizontal", onKeyDown: onKeyDown, className: cn("relative isolate flex h-[50px] w-max items-center rounded-pill border border-primary p-1", className), ...props, children: [_jsx("span", { ref: indicatorRef, "aria-hidden": "true", className: cn("pointer-events-none absolute left-0 top-0 z-0 rounded-pill bg-primary", "h-[var(--tab-pill-h,0px)] w-[var(--tab-pill-w,0px)]", "translate-x-[var(--tab-pill-x,0px)] translate-y-[var(--tab-pill-y,0px)]", 
                // Held invisible until the first measurement, otherwise the pill
                // flies in from the strip's origin on mount.
                measured ? "opacity-100" : "opacity-0", 
                // Tailwind's translate utilities set `translate`, not `transform`.
                "transition-[translate,width,height,opacity] duration-[420ms] ease-out motion-reduce:transition-none") }), children] }));
});
TabsList.displayName = "TabsList";
const TabsTrigger = React.forwardRef(({ value, className, children, onClick, ...props }, ref) => {
    const ctx = useTabs("TabsTrigger");
    const isActive = ctx.value === value;
    return (_jsx("button", { ref: ref, type: "button", role: "tab", "data-tab-value": value, id: triggerId(ctx.baseId, value), "aria-selected": isActive, "aria-controls": panelId(ctx.baseId, value), 
        // Roving tabindex: the strip is one tab stop, and the arrow keys move
        // within it.
        tabIndex: isActive ? 0 : -1, onClick: (event) => {
            onClick?.(event);
            ctx.setValue(value);
        }, className: cn("relative z-[1] h-10 cursor-pointer whitespace-nowrap rounded-pill px-6", "font-body text-sm font-bold uppercase leading-none tracking-[0.08em]", "transition-colors motion-reduce:transition-none", "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", isActive ? "text-primary-foreground" : "text-primary", className), style: { transitionDuration: `${GLIDE_MS}ms` }, ...props, children: children }));
});
TabsTrigger.displayName = "TabsTrigger";
const TabsPanel = React.forwardRef(({ value, className, children, ...props }, ref) => {
    const ctx = useTabs("TabsPanel");
    const isActive = ctx.value === value;
    return (_jsx("div", { ref: ref, role: "tabpanel", id: panelId(ctx.baseId, value), "aria-labelledby": triggerId(ctx.baseId, value), hidden: !isActive, 
        // The panel itself is focusable so keyboard users can tab off the strip
        // straight into what it revealed, per the APG.
        tabIndex: isActive ? 0 : undefined, className: cn("w-full focus-visible:outline-none", className), ...props, children: isActive && children }));
});
TabsPanel.displayName = "TabsPanel";
export { Tabs, TabsList, TabsTrigger, TabsPanel };
//# sourceMappingURL=tabs.js.map